// LanguageControlsGrid.tsx
import React, { useState, useEffect, useMemo } from "react";
import { CourseMetadataItem, fetchCourseOrderData } from "../../API/Course/CourseAPI";
import styles from "./LanguageControlsGrid.module.css";

// Language mapping for API calls
const LANG_CODE_MAP: Record<string, string> = {
  English: "EN",
  Spanish: "ES",
  Italian: "IT",
  French: "FR",
  German: "DE",
  Portuguese: "PT",
};

// --- Language Constants ---
const AVAILABLE_LANGUAGES = [
  "English",
  "Spanish",
  "Italian",
  "French",
  "German",
  "Portuguese",
];
const PROFICIENCY_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const DIFFICULTIES = ["1", "2", "3"];
const DOMAINS = ["general", "healthcare", "construction", "hospitality"];

interface LanguageControlsGridProps {
  // State values
  sourceLanguage: string;
  section: string;
  domain: string;
  proficiencyLevel: string;
  difficulty: string;
  
  // Loading states
  isGenerating?: boolean;
  isTranslating?: boolean;
  isSaving?: boolean;
  loadingVocabulary?: boolean;
  
  // Event handlers
  onSourceLanguageChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onSectionChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onDomainChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onProficiencyChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onDifficultyChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  
  // Optional customizations
  sectionLabel?: string;
  showInfoMessage?: boolean;
  error?: string | null;
  onSectionsLoaded?: (sections: CourseMetadataItem[]) => void;
}

const LanguageControlsGrid: React.FC<LanguageControlsGridProps> = ({
  sourceLanguage,
  section,
  domain,
  proficiencyLevel,
  difficulty,
  isGenerating = false,
  isTranslating = false,
  isSaving = false,
  loadingVocabulary = false,
  onSourceLanguageChange,
  onSectionChange,
  onDomainChange,
  onProficiencyChange,
  onDifficultyChange,
  sectionLabel = "Section (for vocab Browse):",
  showInfoMessage = true,
  error = null,
  onSectionsLoaded,
}) => {
  // Internal state for course sections
  const [courseSections, setCourseSections] = useState<CourseMetadataItem[]>([]);
  const [loadingSections, setLoadingSections] = useState<boolean>(false);
  const [sectionsError, setSectionsError] = useState<string | null>(null);
  
  const langCodeMap = useMemo(() => LANG_CODE_MAP, []);
  
  // Load course sections when sourceLanguage or domain changes
  useEffect(() => {
    if (!sourceLanguage) {
      setCourseSections([]);
      return;
    }

    setLoadingSections(true);
    setSectionsError(null);
    setCourseSections([]);

    const langCode = langCodeMap[sourceLanguage];
    if (!langCode) {
      setSectionsError(`Invalid language: ${sourceLanguage}`);
      setLoadingSections(false);
      return;
    }

    fetchCourseOrderData(langCode, domain)
      .then((raw) => {
        const processed = raw
          .map((item: CourseMetadataItem) => ({
            Identifier: item.Identifier,
            Vocabulary_List: item.Vocabulary_List,
          }))
          .sort((a, b) => +a.Identifier - +b.Identifier);
        setCourseSections(processed);
        onSectionsLoaded?.(processed);
      })
      .catch((err) => {
        setSectionsError(err.message || String(err));
        setCourseSections([]);
      })
      .finally(() => setLoadingSections(false));
  }, [sourceLanguage, domain, langCodeMap, onSectionsLoaded]);
  
  const isDisabled = isGenerating || isTranslating || isSaving || loadingSections;
  const isSectionDisabled = !sourceLanguage || loadingSections || isGenerating || isTranslating || isSaving;

  return (
    <>
      {/* Language Controls Grid */}
      <div className={styles.controlsGrid}>
        <div className={styles.formControl}>
          <label htmlFor="sourceLanguage" className={styles.label}>
            Target Language:
          </label>
          <select
            id="sourceLanguage"
            value={sourceLanguage}
            onChange={onSourceLanguageChange}
            className={styles.input}
            disabled={isDisabled}
          >
            <option value="">Select Language</option>
            {AVAILABLE_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>
        
        <div className={styles.formControl}>
          <label htmlFor="domain" className={styles.label}>
            Domain:
          </label>
          <select
            id="domain"
            value={domain}
            onChange={onDomainChange}
            className={styles.input}
            disabled={isDisabled}
          >
            {DOMAINS.map((d) => (
              <option key={d} value={d}>
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formControl}>
          <label htmlFor="section" className={styles.label}>
            {sectionLabel}
          </label>
          <select
            id="section"
            value={section}
            onChange={onSectionChange}
            className={styles.input}
            disabled={isSectionDisabled}
          >
            <option value="">
              {loadingSections
                ? "Loading Sections..."
                : sourceLanguage
                ? "Select Section"
                : "Choose Language First"}
            </option>
            {!loadingSections &&
              courseSections.map((cs) => {
                const key = cs.Identifier;
                const value = cs.Vocabulary_List;
                const displayText = cs.Vocabulary_List;
                const idText = cs.Identifier;

                return (
                  <option key={key} value={value}>
                    {displayText} (ID: {idText})
                  </option>
                );
              })}
          </select>
        </div>
        
        <div className={styles.formControl}>
          <label htmlFor="proficiencyLevel" className={styles.label}>
            Proficiency Level:
          </label>
          <select
            id="proficiencyLevel"
            value={proficiencyLevel}
            onChange={onProficiencyChange}
            className={styles.input}
            disabled={isDisabled}
          >
            {PROFICIENCY_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>
        
        <div className={styles.formControl}>
          <label htmlFor="difficulty" className={styles.label}>
            Difficulty (1-3):
          </label>
          <select
            id="difficulty"
            value={difficulty}
            onChange={onDifficultyChange}
            className={styles.input}
            disabled={isDisabled}
          >
            {DIFFICULTIES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error Messages */}
      {sectionsError && (
        <div className={styles.errorMessage}>
          Error loading sections: {sectionsError}
        </div>
      )}
      
      {/* Info Messages */}
      {showInfoMessage && !loadingSections &&
        sourceLanguage &&
        courseSections.length === 0 &&
        !error && !sectionsError && (
          <p className={styles.infoMessage}>
            No course sections found for {sourceLanguage}.
          </p>
        )}
    </>
  );
};

export default LanguageControlsGrid;
export { AVAILABLE_LANGUAGES, PROFICIENCY_LEVELS, DIFFICULTIES, DOMAINS };